import React, { useRef, useState } from "react";
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, FormControl,
  InputLabel, MenuItem, Select, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import axios from "axios";
import { commitQuickTrainerBulk, previewQuickTrainerBulk } from "./trainerInventoryApi";
import { isExactTrainerCard, validQuickUnitCost } from "./quickTrainerIntakeUtils";
import QuickTrainerConfirmDialog from "./QuickTrainerConfirmDialog";
import {
  BULK_COLUMNS, buildBulkCommitRows, bulkRowsToTsv, makeBulkOperationId,
  parseQuickBulkTsv, persistPendingQuickBulk, readPendingQuickBulk,
} from "./quickTrainerBulkUtils";

const FINISHES = ["Normal", "Reverse Holo", "Holo"];
const errorText = (error) => error?.response?.data?.error || error?.message || "Request failed";

/** AI parses photographs outside the app; this component never analyses or uploads images. */
export default function QuickTrainerBulkPaste({ batchId, batchOpen, details, costReviewed, disabled, onCommitted, onPendingChange }) {
  const [text, setText] = useState("");
  const [draft, setDraft] = useState([]);
  const [preview, setPreview] = useState(null);
  const [approved, setApproved] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [pending, setPending] = useState(readPendingQuickBulk);
  const [discardDialog, setDiscardDialog] = useState(false);
  const [searchRow, setSearchRow] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [matches, setMatches] = useState([]);
  const inFlight = useRef(false);
  const canWork = batchOpen && !disabled && !busy && !pending && costReviewed && validQuickUnitCost(details.unitCost);
  const validItems = preview?.items?.filter((item) => item.status === "matched") || [];
  const approvedQuantity = preview?.items?.filter((item) => approved.includes(item.index))
    .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0) || 0;

  const clearPreview = () => {
    setPreview(null);
    setApproved([]);
    setSearchRow(null);
    setMatches([]);
    setError("");
    setSuccess("");
  };

  const editRow = (index, field, value) => {
    setDraft((previous) => previous.map((row, i) => i === index
      ? { ...row, [field]: value, parse_error: "" } : row));
    clearPreview();
  };

  const previewRows = async (rows) => {
    if (inFlight.current || !batchId || !batchOpen || pending) return;
    setError("");
    setSuccess("");
    setPreview(null);
    setApproved([]);
    if (rows.length === 0 || rows.length > 50) {
      setError("Paste between 1 and 50 rows in one photograph batch.");
      return;
    }
    if (rows.some((row) => row.parse_error)) {
      setError("Some lines do not have 4–6 TSV columns. Correct them before previewing.");
      return;
    }
    inFlight.current = true;
    setBusy(true);
    try {
      const payloadRows = rows.map((row) => ({
        name_and_number: row.name_and_number,
        set_name: row.set_name,
        card_variant: row.card_variant,
        quantity: row.quantity,
        catalog_card_id: row.catalog_card_id || null,
        source_reference: row.source_reference || details.sourceReference.trim() || null,
        source_type: details.sourceType || "bulk_lot",
        unit_cost: String(details.unitCost),
        storage_location: details.storageLocation.trim() || null,
        notes: details.notes.trim() || null,
      }));
      const response = await previewQuickTrainerBulk(batchId, payloadRows);
      if (response.data?.success !== true || response.data?.items?.length !== rows.length) {
        throw new Error("Preview did not return a result for every card row.");
      }
      setPreview(response.data);
    } catch (requestError) {
      setError(`Preview failed: ${errorText(requestError)}. Nothing has been staged.`);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const parseAndPreview = () => {
    if (!canWork) { setError("Review the batch unit cost before previewing or staging cards."); return; }
    const rows = parseQuickBulkTsv(text);
    setDraft(rows);
    previewRows(rows);
  };

  const findCard = async (index) => {
    if (inFlight.current) return;
    const term = searchText.trim() || draft[index]?.name_and_number || "";
    if (term.length < 2) {
      setError("Enter at least two characters to search the catalogue.");
      return;
    }
    setSearchRow(index);
    setMatches([]);
    setError("");
    try {
      const response = await axios.get("/api/cards/lookup", {
        params: { q: term, limit: 24, high_only: 0 },
      });
      setMatches((response.data?.data || []).filter(isExactTrainerCard));
    } catch (requestError) {
      setError(`Find Card failed: ${errorText(requestError)}`);
    }
  };

  const chooseCard = (index, card) => {
    setDraft((previous) => previous.map((row, i) => i === index
      ? { ...row, name_and_number: card.name_and_number, set_name: card.set_name,
          catalog_card_id: card.id, parse_error: "" } : row));
    clearPreview();
  };

  const rememberPending = (value) => {
    persistPendingQuickBulk(value); // Persist BEFORE the first network request.
    setPending(value);
    onPendingChange(Boolean(value));
  };

  const submit = async (receipt) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const response = await commitQuickTrainerBulk(receipt.batchId, receipt.payload);
      const result = response.data;
      if (result?.success !== true || result.operation_id !== receipt.payload.operation_id ||
          result.accepted_rows !== receipt.payload.rows.length ||
          result.items?.length !== receipt.payload.rows.length) {
        throw new Error("Server response did not confirm all rows. Retry the same operation ID.");
      }
      rememberPending(null);
      const remaining = receipt.sourceRows.filter((_, index) => !receipt.approvedIndices.includes(index));
      setDraft(remaining.map((row, index) => ({ ...row, index })));
      setText(bulkRowsToTsv(remaining));
      setPreview(null);
      setApproved([]);
      setMatches([]);
      setSuccess(`${result.replayed ? "Already staged" : "Staged"}: ${result.accepted_rows} rows / ${result.accepted_quantity} cards. ${remaining.length} row(s) still awaiting review. Physical stock and listings unchanged.`);
      onCommitted(receipt.batchId);
    } catch (requestError) {
      const status = requestError?.response?.status;
      const rejected = requestError?.response?.data?.items;
      const reasons = Array.isArray(rejected) ? rejected.map((item) =>
        `Row ${item.index + 1}: ${item.reason}`).join("; ") : "";
      if (status === 400 || status === 409) {
        // Server guarantees these are pre-commit rejects; don't use a new operation
        // until the operator has inspected the rejected payload and staged list.
        setError(`Bulk commit rejected (no rows staged): ${errorText(requestError)}. ${reasons} Review or discard this saved operation after checking the batch.`);
      } else {
        setError(`Save not confirmed: ${errorText(requestError)}. Retry the SAME operation; do not paste these cards again with a new ID.`);
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const commitApproved = () => {
    if (!canWork || !preview || !approved.length || inFlight.current) return;
    try {
      const rows = buildBulkCommitRows(draft, preview.items, approved, details);
      const receipt = { batchId, sourceRows: draft, approvedIndices: approved,
        payload: { operation_id: makeBulkOperationId(), rows } };
      rememberPending(receipt);
      submit(receipt);
    } catch (err) {
      setError(err.message);
    }
  };

  const discardPending = () => {
    if (inFlight.current) return;
    setDiscardDialog(true);
  };

  const confirmDiscardPending = () => {
    setDiscardDialog(false);
    rememberPending(null);
    setError("");
    setSuccess("Retry discarded; check saved rows before entering these cards again.");
  };

  return (
    <Card variant="outlined"><CardContent>
      <Stack spacing={2}>
        <Typography variant="h6">2. AI photo batch — paste, review, stage</Typography>
        {!costReviewed && !pending && <Alert severity="warning">Review the batch unit cost above before using AI paste.</Alert>}
        <QuickTrainerConfirmDialog open={discardDialog} title="Discard uncertain bulk retry?"
          description="This operation might already have staged cards. Verify the batch before discarding its original operation ID. Cancel preserves the retry." confirmLabel="Discard saved operation ID"
          confirmColor="warning" onCancel={() => setDiscardDialog(false)} onConfirm={confirmDiscardPending} />
        <Typography variant="body2" color="text.secondary">
          Paste one card print per line from Codex. Use tabs, not commas. Nothing is saved during preview.
          For a different photograph use a fresh operation; identical cards can appear in different photos.
        </Typography>
        <Typography variant="caption" sx={{ overflowWrap: "anywhere" }}>
          TSV columns: {BULK_COLUMNS}
        </Typography>
        <TextField multiline minRows={3} maxRows={9} fullWidth size="small" label="AI card rows (tab separated)"
          value={text} onChange={(event) => { setText(event.target.value); setDraft([]); clearPreview(); }}
          disabled={!canWork} placeholder={"Iono 080/091\tPaldean Fates\tNormal\t3\t\tphoto-001"}
          helperText="Full name + printed number, set, exact finish, quantity; catalogue ID/photo ref optional. Maximum 50 rows." />
        <Box><Button variant="outlined" disabled={!canWork || !text.trim()} onClick={parseAndPreview}>
          Preview exact matches (no save)
        </Button></Box>

        {pending && <Alert severity="warning" role="alert">
          <Stack spacing={1}>
            <Typography fontWeight={600}>Bulk submission not yet confirmed</Typography>
            <Typography variant="body2">Batch #{pending.batchId} · {pending.payload.rows.length} rows · Operation: {pending.payload.operation_id}</Typography>
            <Typography variant="body2">An interrupted request might already have succeeded. Retry is safe with the same operation ID, including after refresh.</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button variant="contained" size="small" disabled={busy} onClick={() => submit(pending)}>Retry same bulk commit</Button>
              <Button variant="outlined" size="small" disabled={busy} onClick={discardPending}>Discard after checking batch</Button>
            </Stack>
          </Stack>
        </Alert>}
        {error && <Alert severity="error" role="alert">{error}</Alert>}
        {success && <Alert severity="success" role="status">{success}</Alert>}

        {draft.length > 0 && !pending && <>
          {preview && <Alert severity={preview.pending_rows ? "warning" : "info"}>
            Matched: {preview.matched_rows} rows / {preview.matched_quantity} cards. Needs review: {preview.pending_rows} rows / {preview.pending_quantity} cards.
            Duplicate exact print/finish rows are flagged separately, not silently merged.
          </Alert>}
          <TableContainer sx={{ maxHeight: 490 }}>
            <Table size="small" stickyHeader aria-label="AI trainer bulk preview">
              <TableHead><TableRow>
                <TableCell>Approve</TableCell><TableCell>Exact numbered print</TableCell><TableCell>Set</TableCell>
                <TableCell>Finish</TableCell><TableCell>Qty</TableCell><TableCell>Catalogue ID / photo</TableCell>
                <TableCell>Status</TableCell>
              </TableRow></TableHead>
              <TableBody>{draft.map((row, index) => {
                const result = preview?.items?.[index];
                const isMatched = result?.status === "matched";
                return <TableRow key={index}>
                  <TableCell><Checkbox size="small" aria-label={`Approve row ${index + 1}`} disabled={!canWork || !isMatched}
                    checked={approved.includes(index)} onChange={(event) => setApproved((old) => event.target.checked
                      ? [...old, index] : old.filter((value) => value !== index))} /></TableCell>
                  <TableCell><TextField size="small" label={`Print row ${index + 1}`} value={row.name_and_number}
                    disabled={!canWork} onChange={(event) => editRow(index, "name_and_number", event.target.value)} /></TableCell>
                  <TableCell><TextField size="small" label={`Set row ${index + 1}`} value={row.set_name}
                    disabled={!canWork} onChange={(event) => editRow(index, "set_name", event.target.value)} /></TableCell>
                  <TableCell><FormControl size="small" sx={{ minWidth: 145 }}><InputLabel id={`bulk-finish-${index}`}>Finish</InputLabel>
                    <Select labelId={`bulk-finish-${index}`} label="Finish" value={row.card_variant}
                      disabled={!canWork} onChange={(event) => editRow(index, "card_variant", event.target.value)}>
                      <MenuItem value=""><em>Choose</em></MenuItem>
                      {FINISHES.map((finish) => <MenuItem key={finish} value={finish}>{finish}</MenuItem>)}
                    </Select></FormControl></TableCell>
                  <TableCell><TextField size="small" label={`Qty row ${index + 1}`} type="number" sx={{ width: 90 }}
                    inputProps={{ min: 1, step: 1 }} value={row.quantity} disabled={!canWork}
                    onChange={(event) => editRow(index, "quantity", event.target.value)} /></TableCell>
                  <TableCell><Stack spacing={0.5}>
                    <Typography variant="caption">{row.catalog_card_id ? `ID ${row.catalog_card_id}` : "No ID"}</Typography>
                    <TextField size="small" label="Photo reference" value={row.source_reference || ""} disabled={!canWork}
                      onChange={(event) => editRow(index, "source_reference", event.target.value)} />
                  </Stack></TableCell>
                  <TableCell sx={{ minWidth: 210 }}>
                    {isMatched ? <Stack spacing={0.5}><Chip color="success" size="small" label="Exact match" />
                      <Typography variant="caption">ID {result.match.id}</Typography>
                      {result.duplicate_in_photo && <Chip color="warning" size="small" label="Duplicate in photo — check count" />}
                    </Stack> : <Stack spacing={1}>
                      <Chip size="small" color="warning" label={result ? "Review required" : "Needs preview"} />
                      {result?.reason && <Typography variant="caption">{result.reason}</Typography>}
                      {row.parse_error && <Typography variant="caption" color="error">{row.parse_error}</Typography>}
                      <TextField size="small" label={`Find Card row ${index + 1}`} value={searchRow === index ? searchText : ""}
                        onChange={(event) => { setSearchRow(index); setSearchText(event.target.value); setMatches([]); }}
                        disabled={!canWork} />
                      <Button size="small" disabled={!canWork} onClick={() => findCard(index)}>Find exact card</Button>
                      {searchRow === index && matches.map((candidate) => <Button key={candidate.id} size="small"
                        variant="outlined" disabled={!canWork} onClick={() => chooseCard(index, candidate)}>
                        Select {candidate.name_and_number} · {candidate.set_name} · #{candidate.id}
                      </Button>)}
                    </Stack>}
                  </TableCell>
                </TableRow>;
              })}</TableBody>
            </Table>
          </TableContainer>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
            <Button variant="outlined" disabled={!canWork} onClick={() => previewRows(draft)}>Recheck edited rows</Button>
            <Button variant="outlined" disabled={!canWork || !validItems.length}
              onClick={() => setApproved(validItems.map((item) => item.index))}>Approve all exact matches</Button>
            <Button variant="contained" disabled={!canWork || !approved.length || !preview}
              onClick={commitApproved}>Stage {approved.length} approved row(s) · {approvedQuantity} card(s)</Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Only approved, server-matched rows are submitted. Unresolved rows remain here for correction. Commit stages unallocated rows atomically; it does not receive physical stock or allocate listings.
          </Typography>
        </>}
      </Stack>
    </CardContent></Card>
  );
}
